import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { $ } from "react-jquery-plugin";
import { isValidPhoneNumber } from "libphonenumber-js";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { validateEmail } from "../../../utils/Validation";
import EmailInputWithDomainSuggest from "../../../customComponents/EmailInputWithDomainSuggest";
import AuthPhoneField from "../../../customComponents/AuthPhoneField";
import {
  getPasswordRuleMeta,
  validatePasswordAgainstRules,
} from "../../../utils/authPasswordRules";
import "../LoginPage/login-wizard.css";
import "../SignupPage/signup-wizard.css";
import "./reset-password.css";

const GATE_STYLE_PASSWORD_HINTS = [
  { id: "minLength", label: "At least 8 characters", test: (p) => p.length >= 8 },
  { id: "hasNumber", label: "At least 1 number", test: (p) => /\d/.test(p) },
  { id: "hasLetter", label: "At least 1 letter", test: (p) => /[a-zA-Z]/.test(p) },
];

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlStep = searchParams.get("step");
  const step = urlStep === "2" ? "reset" : "identify";

  const [accountTab, setAccountTab] = useState("email");
  const [countryCode, setCountryCode] = useState("+91");
  const [signId, setSignId] = useState("");
  const [otpSingle, setOtpSingle] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [otpResendBusy, setOtpResendBusy] = useState(false);
  const [sendOtpBusy, setSendOtpBusy] = useState(false);
  const [resetBusy, setResetBusy] = useState(false);
  const [identifierError, setIdentifierError] = useState(false);
  const [otpError, setOtpError] = useState(false);
  const [confirmError, setConfirmError] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const otpInputRefs = useRef([]);

  const goToStep = (nextStep, { replace = false } = {}) => {
    const value = nextStep === "reset" ? "2" : "1";
    setSearchParams({ step: value }, { replace });
  };

  useEffect(() => {
    $("body").addClass("loginbg");
    const keepClassId = window.setInterval(() => {
      if (window.location.pathname === "/forgot_password") {
        $("body").addClass("loginbg");
      }
    }, 500);
    return () => {
      window.clearInterval(keepClassId);
      $("body").removeClass("loginbg");
    };
  }, []);

  useEffect(() => {
    if (step === "reset" && !otpSent) {
      goToStep("identify", { replace: true });
    }
  }, [step, otpSent]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step, accountTab]);

  useEffect(() => {
    if (otpTimer <= 0) return undefined;
    const timer = setInterval(() => setOtpTimer((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(timer);
  }, [otpTimer]);

  useEffect(() => {
    if (step === "reset") {
      window.setTimeout(() => otpInputRefs.current[0]?.focus(), 120);
    }
  }, [step]);

  const usernamePart = useMemo(() => {
    if (accountTab === "email" && signId.includes("@")) {
      return signId.split("@")[0];
    }
    return String(signId || "").replace(/\D/g, "");
  }, [accountTab, signId]);

  const passwordRules = useMemo(() => getPasswordRuleMeta(usernamePart), [usernamePart]);
  const passwordMeetsAllRules = useMemo(() => {
    const p = String(password || "");
    if (!p.trim()) return false;
    return passwordRules.every((r) => r.passes(p));
  }, [password, passwordRules]);

  const buildSignIdPayload = () => {
    if (accountTab === "email") {
      if (validateEmail(signId) !== undefined) {
        alertErrorMessage("Please enter a valid email address");
        return null;
      }
      return signId.trim();
    }

    const fullPhone = `${countryCode}${signId}`;
    if (!isValidPhoneNumber(fullPhone)) {
      alertErrorMessage("Please enter a valid phone number for the selected country");
      return null;
    }
    return `${countryCode} ${signId}`;
  };

  const maskedDestination = useMemo(() => {
    if (accountTab === "email") {
      const email = signId.trim();
      if (!email.includes("@")) return email;
      const [u, d] = email.split("@");
      if (u.length <= 2) return `${u[0]}***@${d}`;
      return `${u[0]}***${u.slice(-1)}@${d}`;
    }
    const raw = String(signId || "");
    if (raw.length <= 4) return `${countryCode} ${raw}`;
    return `${countryCode} ${raw.slice(0, 2)}***${raw.slice(-2)}`;
  }, [accountTab, signId, countryCode]);

  const getOtpDigitsStr = () => otpSingle.replace(/\D/g, "").slice(0, 6);

  const focusOtpIndex = (idx) => {
    requestAnimationFrame(() => {
      otpInputRefs.current[Math.min(Math.max(0, idx), 5)]?.focus();
    });
  };

  const handleSendOtp = async () => {
    const payload = buildSignIdPayload();
    if (!payload) {
      setIdentifierError(true);
      return;
    }

    setIdentifierError(false);
    setSendOtpBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.getOtp(payload, "forgot");
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent.");
        setOtpSingle("");
        setOtpTimer(60);
        setOtpSent(true);
        goToStep("reset");
      } else {
        alertErrorMessage(result?.message || "Could not send verification code.");
      }
    } catch (err) {
      alertErrorMessage(err?.message || "Could not send verification code.");
    } finally {
      setSendOtpBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpTimer > 0 || otpResendBusy) return;
    const payload = buildSignIdPayload();
    if (!payload) return;

    setOtpResendBusy(true);
    try {
      const result = await AuthService.getOtp(payload, "forgot");
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code resent.");
        setOtpTimer(60);
        setOtpSingle("");
        focusOtpIndex(0);
      } else {
        alertErrorMessage(result?.message || "Could not resend verification code.");
      }
    } catch (err) {
      alertErrorMessage(err?.message || "Could not resend verification code.");
    } finally {
      setOtpResendBusy(false);
    }
  };

  const handleOtpCellChange = (index, e) => {
    if (otpError) setOtpError(false);
    const raw = String(e.target.value ?? "").replace(/\D/g, "");
    const d = getOtpDigitsStr();
    const before = d.slice(0, index);
    const after = d.slice(index + 1);
    const merged = (before + raw + after).replace(/\D/g, "").slice(0, 6);
    setOtpSingle(merged);
    focusOtpIndex(Math.min(index + raw.length, 5));
  };

  const handleOtpCellKeyDown = (index, e) => {
    if (otpError) setOtpError(false);
    const d = getOtpDigitsStr();
    if (e.key === "Backspace") {
      if (d[index]) {
        e.preventDefault();
        setOtpSingle(d.slice(0, index) + d.slice(index + 1));
      } else if (index > 0) {
        e.preventDefault();
        setOtpSingle(d.slice(0, index - 1) + d.slice(index));
        focusOtpIndex(index - 1);
      }
    }
  };

  const handleOtpRowPaste = (e) => {
    const text = e.clipboardData?.getData("text") || "";
    const raw = text.replace(/\D/g, "").slice(0, 6);
    if (!raw) return;
    e.preventDefault();
    setOtpError(false);
    setOtpSingle(raw);
    focusOtpIndex(Math.min(raw.length, 5));
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const code = getOtpDigitsStr();
    if (code.length < 6) {
      setOtpError(true);
      alertErrorMessage("Please enter the 6-digit verification code.");
      return;
    }

    const passwordCheck = validatePasswordAgainstRules(password, passwordRules);
    if (!passwordCheck.ok) {
      alertErrorMessage(passwordCheck.message);
      return;
    }

    if (password !== confirmPassword) {
      setConfirmError(true);
      alertErrorMessage("Passwords do not match.");
      return;
    }

    setConfirmError(false);
    setResetBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const result = await AuthService.forgotPassword(
        { signId, otp: code, password },
        accountTab === "phone" ? "phone" : "email",
        countryCode
      );
      if (result?.success) {
        alertSuccessMessage(result?.message || "Password reset successfully.");
        navigate("/login", { replace: true });
      } else {
        alertErrorMessage(result?.message || "Password reset failed.");
      }
    } catch (err) {
      alertErrorMessage(err?.message || "Password reset failed.");
    } finally {
      setResetBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const gateHintStates = useMemo(() => {
    const p = String(password || "");
    if (!p.trim()) return GATE_STYLE_PASSWORD_HINTS.map(() => "idle");
    return GATE_STYLE_PASSWORD_HINTS.map((hint) => (hint.test(p) ? "ok" : "bad"));
  }, [password]);

  const switchTab = (tab) => {
    if (tab === accountTab) return;
    setAccountTab(tab);
    setSignId("");
    setOtpSingle("");
    setPassword("");
    setConfirmPassword("");
    setOtpSent(false);
    goToStep("identify");
    setIdentifierError(false);
    setOtpError(false);
    setConfirmError(false);
    setOtpTimer(0);
  };

  const otpDigitsDisplay = getOtpDigitsStr();

  return (
    <>
      <Helmet>
        <title>Reset Password – AGCX</title>
        <meta
          name="description"
          content="Reset your AGCX account password securely using email or phone verification."
        />
      </Helmet>

      <div className="login_fullhieght login-page-center">
        <div className="login_section m-auto forgot_password reset-password-page">
          <div className="login_form_right">
            <div className="form_block_login login-wizard-root">
              <div className="reset-password-notice" role="note">
                <i className="ri-information-line" aria-hidden />
                <p>
                  For the security of your assets, withdrawals will be disabled for 24 hours after a password reset.
                </p>
              </div>

              <h1 className="login-wizard-title">Reset Your Password</h1>

              {step === "identify" ? (
                <>
                  <ul className="login-wizard-tabs" role="tablist" aria-label="Reset method">
                    <li>
                      <button
                        type="button"
                        className={accountTab === "email" ? "is-active" : ""}
                        role="tab"
                        aria-selected={accountTab === "email"}
                        onClick={() => switchTab("email")}
                      >
                        Email/Username
                      </button>
                    </li>
                    <li>
                      <button
                        type="button"
                        className={accountTab === "phone" ? "is-active" : ""}
                        role="tab"
                        aria-selected={accountTab === "phone"}
                        onClick={() => switchTab("phone")}
                      >
                        Phone
                      </button>
                    </li>
                  </ul>

                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (!sendOtpBusy) void handleSendOtp();
                    }}
                    noValidate
                  >
                    <div className="row">
                      {accountTab === "email" ? (
                        <div className="col-sm-12 input_block">
                          {/* <label htmlFor="reset-email">Email*</label> */}
                          <EmailInputWithDomainSuggest
                            id="reset-email"
                            name="signId"
                            value={signId}
                            onChange={(e) => {
                              setIdentifierError(false);
                              setSignId(e.target.value);
                            }}
                            wrapClassName={`email_code eids-email-wrap${identifierError ? " signup-step1-email--error" : ""}`}
                            placeholder="Enter your email"
                            autoComplete="email"
                          />
                        </div>
                      ) : (
                        <div className={`col-sm-12 input_block${identifierError ? " login-phone-wrap--error" : ""}`}>
                          {/* <label htmlFor="resetPasswordCountryCode">Phone*</label> */}
                          <AuthPhoneField
                            inputId="resetPasswordCountryCode"
                            countryCode={countryCode}
                            onCountryChange={setCountryCode}
                            phone={signId}
                            onPhoneChange={(v) => {
                              setIdentifierError(false);
                              setSignId(v);
                            }}
                            phonePlaceholder="Enter your phone number"
                          />
                        </div>
                      )}

                      <div className="col-sm-12 login_btn">
                        <button
                          type="submit"
                          className="login-wizard-next-btn"
                          disabled={sendOtpBusy}
                          aria-busy={sendOtpBusy}
                        >
                          {sendOtpBusy ? (
                            <>
                              <span className="login-wizard-next-btn__spinner" aria-hidden />
                              <span>Sending code…</span>
                            </>
                          ) : (
                            "Next"
                          )}
                        </button>
                      </div>

                      <div className="col-sm-12 registration__info mt-3 reset-password-login-link">
                        <p>
                          <Link to="/login">Back to Login</Link>
                        </p>
                      </div>
                    </div>
                  </form>
                </>
              ) : (
                <form onSubmit={handleResetPassword} noValidate>
                  <div className="row">
                    <div className="col-sm-12 input_block">
                      <p className="reset-password-subtitle reset-password-subtitle--step2">
                        Enter the verification code sent to <strong>{maskedDestination}</strong>
                      </p>
                    </div>

                    <div className="col-sm-12 input_block">
                      <label htmlFor="reset-otp-cell-0">Verification code</label>
                      <div
                        className={`signup-wizard-otp-stack${otpError ? " signup-wizard-otp-stack--error" : ""}`}
                        onPaste={handleOtpRowPaste}
                        role="group"
                        aria-label="6-digit verification code"
                      >
                        <div className="signup-wizard-otp-row">
                          {Array.from({ length: 6 }).map((_, idx) => (
                            <input
                              key={idx}
                              id={idx === 0 ? "reset-otp-cell-0" : undefined}
                              ref={(el) => {
                                otpInputRefs.current[idx] = el;
                              }}
                              className="signup-wizard-otp-cell"
                              type="text"
                              inputMode="numeric"
                              autoComplete={idx === 0 ? "one-time-code" : "off"}
                              maxLength={1}
                              value={otpDigitsDisplay[idx] || ""}
                              onChange={(e) => handleOtpCellChange(idx, e)}
                              onKeyDown={(e) => handleOtpCellKeyDown(idx, e)}
                              aria-label={`Digit ${idx + 1}`}
                            />
                          ))}
                        </div>
                        <div className="signup-wizard-otp-actions">
                          <button
                            type="button"
                            className="signup-wizard-otp-link"
                            disabled={otpTimer > 0 || otpResendBusy}
                            onClick={() => void handleResendOtp()}
                          >
                            {otpResendBusy
                              ? "Resending…"
                              : otpTimer > 0
                                ? `Resend in ${otpTimer}s`
                                : "Resend code"}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="col-sm-12 input_block">
                      <label htmlFor="reset-new-password">New Password</label>
                      <div
                        className={`email_code signup-wizard-password-field${password.trim() ? "" : " signup-wizard-password-field--empty"}`}
                      >
                        <input
                          id="reset-new-password"
                          className="input_filed"
                          type={showPassword ? "text" : "password"}
                          placeholder="New Password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoComplete="new-password"
                          style={{ paddingRight: password.trim() ? 44 : 14 }}
                        />
                        {password.trim().length > 0 && (
                          <div
                            className="get_otp otp2 signup-wizard-password-toggle"
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowPassword((s) => !s)}
                            onKeyDown={(e) => e.key === "Enter" && setShowPassword((s) => !s)}
                          >
                            {showPassword ? <i className="ri-eye-line" /> : <i className="ri-eye-off-line" />}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="col-sm-12 input_block">
                      <label htmlFor="reset-confirm-password">Enter Password Again</label>
                      <div
                        className={`email_code signup-wizard-password-field${confirmPassword.trim() ? "" : " signup-wizard-password-field--empty"}${confirmError ? " login-wizard-password--error" : ""}`}
                      >
                        <input
                          id="reset-confirm-password"
                          className="input_filed"
                          type={showConfirmPassword ? "text" : "password"}
                          placeholder="Enter Password Again"
                          value={confirmPassword}
                          onChange={(e) => {
                            setConfirmError(false);
                            setConfirmPassword(e.target.value);
                          }}
                          autoComplete="new-password"
                          style={{ paddingRight: confirmPassword.trim() ? 44 : 14 }}
                        />
                        {confirmPassword.trim().length > 0 && (
                          <div
                            className="get_otp otp2 signup-wizard-password-toggle"
                            role="button"
                            tabIndex={0}
                            onClick={() => setShowConfirmPassword((s) => !s)}
                            onKeyDown={(e) => e.key === "Enter" && setShowConfirmPassword((s) => !s)}
                          >
                            {showConfirmPassword ? <i className="ri-eye-line" /> : <i className="ri-eye-off-line" />}
                          </div>
                        )}
                      </div>
                    </div>

                    <div
                      className={`col-sm-12 input_block reset-password-gate-hints${password.trim() ? "" : " reset-password-gate-hints--empty"}`}
                      aria-live="polite"
                    >
                      {GATE_STYLE_PASSWORD_HINTS.map((hint, idx) => {
                        const st = gateHintStates[idx];
                        const iconClass =
                          st === "ok"
                            ? "ri-checkbox-circle-fill"
                            : st === "bad"
                              ? "ri-close-circle-fill"
                              : "ri-checkbox-blank-circle-line";
                        return (
                          <div key={hint.id} className={`reset-password-gate-hint reset-password-gate-hint--${st}`}>
                            <i className={iconClass} aria-hidden />
                            <span>{hint.label}</span>
                          </div>
                        );
                      })}
                    </div>

                    <div className="col-sm-12 login_btn">
                      <button
                        type="submit"
                        className={`signup-wizard-confirm-password${resetBusy ? " signup-wizard-confirm-password--busy" : ""}`}
                        disabled={!passwordMeetsAllRules || !confirmPassword.trim() || resetBusy}
                        aria-busy={resetBusy}
                      >
                        {resetBusy ? (
                          <>
                            <span className="signup-confirm-btn__spinner" aria-hidden />
                            Resetting…
                          </>
                        ) : (
                          "Reset"
                        )}
                      </button>
                    </div>

                    <div className="col-sm-12 registration__info mt-3 reset-password-login-link">
                      <p>
                        <Link to="/login">Back to Login</Link>
                      </p>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
